import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { THEME } from "@/config/theme";
import { MCP_CLIENTS, DEFAULT_CLIENT_ID, renderSnippet } from "@/config/mcpClients";
import { mcpUrl } from "@/config/api";

interface Props {
  /**
   * The plaintext key, when we still have it. Supplied only in the dialog shown
   * right after minting; the standalone section on the page passes nothing and
   * renders the placeholder, because by then the token is unrecoverable.
   */
  token?: string;
}

/**
 * Client picker + copyable setup snippet. Rendered twice with different props,
 * which is the whole reason the token is optional rather than two components.
 */
export default function SetupSnippet({ token }: Props) {
  const [clientId, setClientId] = useState(DEFAULT_CLIENT_ID);
  const [copied, setCopied] = useState(false);

  const client = MCP_CLIENTS.find((c) => c.id === clientId) ?? MCP_CLIENTS[0]!;
  const code = renderSnippet(client, mcpUrl(), token);

  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard can be blocked by permissions or a non-secure origin. The
      // snippet is selectable, so failing silently is better than an alert.
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <label htmlFor="mcp-client" className={`${THEME.pageCard.label} ${THEME.mcpPage.fieldLabel}`}>
          Client
        </label>
        <select
          id="mcp-client"
          aria-label="MCP client"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          className={`${THEME.panel.input} ${THEME.mcpPage.control} rounded-lg px-2.5 py-1.5 flex-1 max-w-[220px]`}
        >
          {MCP_CLIENTS.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </select>
      </div>

      <p className={THEME.mcpPage.hint}>{client.target}</p>

      <div className="relative">
        <pre
          data-testid="mcp-snippet"
          className={`${THEME.pageCard.snippetBlock} ${THEME.mcpPage.code} ${THEME.scrollbar.dark} rounded-lg p-3.5 pr-11 overflow-x-auto whitespace-pre leading-relaxed`}
        >
          {code}
        </pre>
        <button
          type="button"
          onClick={copy}
          aria-label="Copy snippet"
          className={`absolute top-2 right-2 p-1.5 rounded-md ${THEME.button.ghost} transition-colors`}
        >
          {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
        </button>
      </div>
    </div>
  );
}
